import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, MessageCircle, Clock, ShieldCheck, ArrowRight } from "lucide-react";
import { publicApi } from "../lib/api";
import { useLenis } from "../lib/useLenis";
import { DEFAULT_SITE_DATA } from "../constants/defaultSiteData";
import SEO from "../components/site/SEO";
import Navbar from "../components/site/Navbar";
import ContactRail from "../components/site/ContactRail";
import PageHero from "../components/site/PageHero";
import LeadForm from "../components/site/LeadForm";
import { Footer } from "../components/site/Sections5";
import { trackContact } from "../lib/metaPixel";

export default function Contact() {
  useLenis();
  const [data, setData] = useState(DEFAULT_SITE_DATA);

  useEffect(() => {
    publicApi.get("/site").then((r) => {
      if (r.data?.content) setData(r.data);
    }).catch(() => {});
  }, []);

  const c = data?.content || DEFAULT_SITE_DATA.content;
  const s = c.settings || {
    phone: "+91 93221 06632",
    whatsapp: "https://wa.me/919322106632",
    email: "admallindiagroup@gmail.com",
    location: "Navi Mumbai, Maharashtra, India",
  };

  return (
    <div className="bg-white min-h-screen flex flex-col" data-testid="contact-page">
      <SEO
        title="Contact ADMALL INDIA | Integrated Advertising Agency in Navi Mumbai"
        description="Connect with ADMALL INDIA for brand engineering, multimedia production, digital marketing, and business growth consultation in Navi Mumbai, Maharashtra."
        canonical="https://admallindia.com/contact"
      />
      <Navbar logo={c.logo_url} />
      <ContactRail settings={s} />

      <main className="flex-grow">
        <PageHero
          category="Connect"
          categoryLink="/contact"
          title="Start A Strategic Growth Conversation"
          tagline="Whether planning an upcoming campaign, revamping your corporate identity, or scaling customer acquisition, our strategy and creative teams are ready to connect."
          bgImage="https://images.unsplash.com/photo-1781127445209-f02336fa36a0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          ctaLabel="Fill Enquiry Form"
          ctaHref="#inquiry-form"
        />

        {/* Contact Hub & Form Grid */}
        <section id="contact-details" className="bg-[#F8F9FA] py-20 md:py-28">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
              
              {/* Left Column: Coordinates & Information */}
              <div className="lg:col-span-5">
                <div className="mb-4 inline-flex items-center gap-2 font-display text-xs font-bold uppercase tracking-widest text-[#FF5722]">
                  <span className="h-2 w-2 rounded-full bg-[#FF5722]" />
                  <span>Headquarters & Channels</span>
                </div>
                <h2 className="font-display text-3xl font-extrabold uppercase leading-tight tracking-tight text-navy sm:text-4xl">
                  Let’s talk commercial strategy, not vague theories.
                </h2>
                <p className="mt-4 font-body text-base text-navy/70 leading-relaxed">
                  We work with ambitious founders, established SMEs, real estate developers, and enterprise brands across India. Reach out directly through any of our verified communication channels.
                </p>

                {/* Direct Channel Cards */}
                <div className="mt-8 space-y-4">
                  <a
                    href={`tel:${s.phone_raw || "+919322106632"}`}
                    onClick={() => trackContact("phone", { location: "contact_page", label: "Direct Phone" })}
                    className="flex items-center gap-4 rounded-2xl border border-navy/10 bg-white p-5 transition hover:border-[#FF5722] hover:shadow-md"
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#FF5722]/10 text-[#FF5722]">
                      <Phone size={22} />
                    </div>
                    <div>
                      <div className="font-body text-xs font-semibold uppercase tracking-wider text-navy/50">Direct Phone</div>
                      <div className="font-display text-lg font-bold text-navy">{s.phone}</div>
                    </div>
                  </a>

                  <a
                    href={s.whatsapp || "https://wa.me/919322106632"}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackContact("whatsapp", { location: "contact_page", label: "WhatsApp Desk" })}
                    className="flex items-center gap-4 rounded-2xl border border-emerald-500/20 bg-white p-5 transition hover:border-emerald-500 hover:shadow-md"
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600">
                      <MessageCircle size={22} />
                    </div>
                    <div>
                      <div className="font-body text-xs font-semibold uppercase tracking-wider text-navy/50">WhatsApp Strategic Desk</div>
                      <div className="font-display text-lg font-bold text-navy">Chat Directly On WhatsApp</div>
                    </div>
                  </a>

                  <a
                    href={`mailto:${s.email || "admallindiagroup@gmail.com"}`}
                    onClick={() => trackContact("email", { location: "contact_page", label: "Email Inquiry" })}
                    className="flex items-center gap-4 rounded-2xl border border-navy/10 bg-white p-5 transition hover:border-[#FF5722] hover:shadow-md"
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <Mail size={22} />
                    </div>
                    <div>
                      <div className="font-body text-xs font-semibold uppercase tracking-wider text-navy/50">Email Inquiry</div>
                      <div className="font-display text-base font-bold text-navy">{s.email}</div>
                    </div>
                  </a>

                  <div className="flex items-center gap-4 rounded-2xl border border-navy/10 bg-white p-5">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy">
                      <MapPin size={22} />
                    </div>
                    <div>
                      <div className="font-body text-xs font-semibold uppercase tracking-wider text-navy/50">Strategic Hub</div>
                      <div className="font-display text-base font-bold text-navy">{s.location}</div>
                    </div>
                  </div>
                </div>

                {/* Assurance & SLA */}
                <div className="mt-8 rounded-2xl border border-navy/10 bg-navy p-6 text-white">
                  <div className="flex items-center gap-3 text-[#FF5722] mb-3">
                    <Clock size={20} />
                    <span className="font-display text-xs font-bold uppercase tracking-widest">Rapid Response Guarantee</span>
                  </div>
                  <p className="font-body text-sm text-white/80 leading-relaxed">
                    Every enquiry is reviewed directly by our senior leadership team. You will receive an initial response and discovery proposal within <strong>24 business hours</strong>.
                  </p>
                </div>
              </div>

              {/* Right Column: Lead Form Container */}
              <div id="inquiry-form" className="lg:col-span-7">
                <div className="rounded-3xl border border-navy/10 bg-white p-6 shadow-sm sm:p-10">
                  <div className="mb-8">
                    <div className="inline-flex items-center gap-2 rounded-full bg-[#FF5722]/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-[#FF5722] mb-3">
                      <ShieldCheck size={14} />
                      <span>Confidential Consultation</span>
                    </div>
                    <h3 className="font-display text-2xl font-extrabold uppercase tracking-tight text-navy sm:text-3xl">
                      Request A Project Discussion
                    </h3>
                    <p className="mt-2 font-body text-sm text-navy/60">
                      Share your requirements and goals. We will prepare relevant sector case studies and discussion frameworks for our initial conversation.
                    </p>
                  </div>

                  <LeadForm compact={false} source="contact_page" />
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* What Happens Next Section */}
        <section className="bg-white py-16 md:py-24 border-t border-navy/10">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <div className="font-display text-xs font-bold uppercase tracking-widest text-[#FF5722] mb-2">Transparency First</div>
              <h3 className="font-display text-3xl font-extrabold uppercase text-navy sm:text-4xl">What Happens Next</h3>
              <p className="font-body text-sm text-navy/60 mt-3">Our 3-step intake process ensures complete clarity before any contracts or commitments.</p>
            </div>

            <div className="grid gap-8 md:grid-cols-3">
              <div className="rounded-2xl border border-navy/10 bg-[#F8F9FA] p-8 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[#FF5722] font-display text-lg font-black text-white">1</div>
                <h4 className="font-display text-lg font-bold uppercase text-navy">Discovery Call</h4>
                <p className="mt-3 font-body text-sm text-navy/60 leading-relaxed">
                  A 30-minute consultation to examine your current product positioning, audience bottlenecks, and immediate commercial targets.
                </p>
              </div>

              <div className="rounded-2xl border border-navy/10 bg-[#F8F9FA] p-8 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-navy font-display text-lg font-black text-white">2</div>
                <h4 className="font-display text-lg font-bold uppercase text-navy">Opportunity Mapping</h4>
                <p className="mt-3 font-body text-sm text-navy/60 leading-relaxed">
                  We analyze competitor positioning, evaluate channel viability, and outline recommended creative and media budgets.
                </p>
              </div>

              <div className="rounded-2xl border border-navy/10 bg-[#F8F9FA] p-8 text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-600 font-display text-lg font-black text-white">3</div>
                <h4 className="font-display text-lg font-bold uppercase text-navy">Tailored Action Plan</h4>
                <p className="mt-3 font-body text-sm text-navy/60 leading-relaxed">
                  You receive an unambiguous project scope with exact deliverables, milestones, and accountable monthly timelines.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer content={c} />
    </div>
  );
}
